/**
 * Toggles the visibility of dynamic fields based on specified conditions.
 *
 * @param {HTMLElement} parent - The parent element containing the dynamic fields and input elements.
 *
 * The function looks for elements within the parent that have the attribute `data-js-dynamic-field`.
 * It parses the JSON conditions specified in this attribute and checks the values of corresponding input fields.
 * If the conditions are met, the dynamic field is shown; otherwise, it is hidden.
 *
 * Example of `data-js-dynamic-field` attribute value:
 * {
 *   "inputName1": "value1",
 *   "inputName2": ["value2", "value3"]
 * }
 *
 * In this example, the dynamic field will be shown if `inputName1` has the value "value1" or if `inputName2` has the value "value2" or "value3".
 *
 *
 * Example of conditional validation handled serverside in Forms.php
 *  - cf7_conditional_state
 */
function showHideDynamicFields(parent) {
  const dynamicFields = parent.querySelectorAll('[data-js-dynamic-field]');

  dynamicFields.forEach(function (field) {
    try {
      const conditions = JSON.parse(
        field.getAttribute('data-js-dynamic-field')
      );

      const showField = canShowField(parent, conditions);

      if (showField) {
        field.classList.remove('u-is-hidden');
      } else {
        field.classList.add('u-is-hidden');
      }
    } catch (e) {
      console.error('Something went wrong.', e);
    }
  });
}

/**
 * Determines whether a field should be shown based on specified conditions.
 *
 * @param {Object} conditions - An object where keys are field names and values are the required values for those fields.
 * @returns {boolean} - Returns true if any of the conditions are met, otherwise false.
 */
function canShowField(form, conditions) {
  let showField = false;

  Object.keys(conditions).forEach((field) => {
    const input = form.querySelector(`[name=${field}]`);
    const requiredValues = conditions[field];

    // Revisit this for other field types
    if (!input) {
      return;
    }

    // Multiple possible values
    if (Array.isArray(requiredValues) && requiredValues.includes(input.value)) {
      showField = true;
      // Single value
    } else if (requiredValues == input.value) {
      showField = true;
    }
  });

  return showField;
}

/**
 * Clears the values of hidden dynamic fields within a form.
 *
 * This function selects all elements within the provided form that have the
 * attribute `data-js-dynamic-field`. If any of these elements have the class
 * `u-is-hidden`, their value is set to an empty string.
 *
 * @param {HTMLFormElement} form - The form element containing the dynamic fields.
 */
function clearHiddenFields(form) {
  const dynamicFields = form.querySelectorAll('[data-js-dynamic-field]');

  dynamicFields.forEach((field) => {
    if (field.classList.contains('u-is-hidden')) {
      field.querySelector('input, select').value = '';
    }
  });
}

showHideDynamicFields(document);

export function init() {
  // Add event listener to watch for changes
  [
    ...new Set(
      [...document.querySelectorAll('[data-js-dynamic-field]')].map((el) =>
        el.closest('form')
      )
    ),
  ].forEach((form) => {
    form.addEventListener('change', () => showHideDynamicFields(form));
    form
      .querySelector('button[type="submit"]')
      .addEventListener('click', () => clearHiddenFields(form));
  });
}
